/* globals document */
import {useRef} from 'react';
import {isNil} from 'lodash';
import {mergeRefs} from '../utils/mergeRefs';
import {modulus} from '../utils/math';
import {useUniqueId} from '../utils/useUniqueId';
import {
  type UseButtonGroupProps,
  type ButtonGroupOption,
  type ButtonGroupItemProps,
  type EnhancedButtonGroupItemPropsWithAriaLabel,
  type EnhancedButtonGroupItemPropsWithLabel,
} from './ButtonGroup.types';
import {UP, DOWN, LEFT, RIGHT, END, HOME} from '@packages/utilities/key-utils';

function createButtonFocuser(node: null) {
  const buttons = node
    ? // @ts-expect-error - TS2339 - Property 'children' does not exist on type 'never'. | TS18046 - 'child' is of type 'unknown'.
      Array.from(node.children).filter((child) => !child.disabled)
    : [];
  const buttonsLength = buttons.length;

  const previous = () => {
    const focusedIndex = buttons.indexOf(document?.activeElement);

    if (focusedIndex === -1) return;

    const nextIndex = modulus(focusedIndex - 1, buttonsLength);
    if (buttons[nextIndex]) {
      // @ts-expect-error - TS2571 - Object is of type 'unknown'.
      buttons[nextIndex].focus();
    }
  };

  const next = () => {
    const focusedIndex = buttons.indexOf(document?.activeElement);

    if (focusedIndex === -1) return;

    const nextIndex = modulus(focusedIndex + 1, buttonsLength);
    if (buttons[nextIndex]) {
      // @ts-expect-error - TS2571 - Object is of type 'unknown'.
      buttons[nextIndex].focus();
    }
  };

  const first = () => {
    if (buttons[0]) {
      // @ts-expect-error - TS2339 - Property 'focus' does not exist on type '{}'.
      buttons[0].focus();
    }
  };

  const last = () => {
    const lastIndex = buttons.length - 1;

    if (buttons[lastIndex]) {
      // @ts-expect-error - TS2571 - Object is of type 'unknown'.
      buttons[lastIndex].focus();
    }
  };

  return {
    first,
    last,
    previous,
    next,
  };
}

// @ts-expect-error - TS7031 - Binding element 'onKeyDown' implicitly has an 'any' type. | TS7031 - Binding element 'onChange' implicitly has an 'any' type. | TS7031 - Binding element 'items' implicitly has an 'any' type. | TS7031 - Binding element 'ref' implicitly has an 'any' type. | TS7031 - Binding element 'radiogroup' implicitly has an 'any' type.
function useKeyboardEvents({onKeyDown, onChange, items, ref, radiogroup}) {
  // @ts-expect-error - TS7006 - Parameter 'i' implicitly has an 'any' type.
  const enabledItems = items.filter((i) => Boolean(!i.disabled));
  const selectFirst = () => {
    const item = enabledItems[0]?.value;
    onChange && onChange(item);
  };
  const selectLast = () => {
    const item = enabledItems[enabledItems.length - 1]?.value;
    onChange && onChange(item);
  };

  const handleOnKeyDown = (event: KeyboardEvent) => {
    const {first, last, previous, next} = createButtonFocuser(ref.current);

    switch (event.keyCode) {
      case HOME:
        event.preventDefault();
        first();
        if (radiogroup) {
          selectFirst();
        }
        break;
      case END:
        event.preventDefault();
        last();
        if (radiogroup) {
          selectLast();
        }
        break;

      /**
       * These handle the "previous" and "next" selection interactions for
       * a roving tab index.
       *
       * @see https://www.w3.org/TR/wai-aria-practices-1.1/examples/radio/radio-1/radio-1.html
       */
      case UP:
      case LEFT:
        if (radiogroup) return;
        event.preventDefault();
        previous();
        break;

      case DOWN:
      case RIGHT:
        if (radiogroup) return;
        event.preventDefault();
        next();
        break;

      default:
        break;
    }
    onKeyDown && onKeyDown(event);
  };

  return {
    onKeyDown: handleOnKeyDown,
  };
}

export function useButtonGroup(props: UseButtonGroupProps) {
  const {
    disabled,
    name: nameProp,
    id: idProp,
    onBlur,
    onChange,
    onFocus,
    onKeyDown,
    onMouseEnter,
    onMouseLeave,
    ref,
    options = [],
    radiogroup,
    sizing = 'medium',
    value,
    tabStyles = {},
    ...rest
  } = props;

  const buttonGroupRef = useRef(null);
  const id = useUniqueId(useButtonGroup, 'button-group', idProp);
  const name = useUniqueId(useButtonGroup, 'button-group', nameProp);

  const keyboardHandlers = useKeyboardEvents({
    ref: buttonGroupRef,
    onKeyDown,
    items: options,
    radiogroup,
    onChange,
  });

  const handleOnChange = (itemValue: string) => onChange && onChange(itemValue);
  const handleOnKeyDown = (event: KeyboardEvent) =>
    onKeyDown && onKeyDown(event);
  const handleOnFocus = (event: Event) => onFocus && onFocus(event);
  const handleOnBlur = (event: Event) => onBlur && onBlur(event);
  const handleOnMouseEnter = (event: MouseEvent) =>
    onMouseEnter && onMouseEnter(event);
  const handleOnMouseLeave = (event: MouseEvent) =>
    onMouseLeave && onMouseLeave(event);

  const getBorderRadius = (index: number) => {
    switch (index) {
      case 0:
        return '2px 0 0 2px';
      case options.length - 1:
        return '0 2px 2px 0';
      default:
        return '0';
    }
  };

  const getItemProps = ({
    item,
    index,
  }: {
    item: ButtonGroupOption;
    index: number;
  }): ButtonGroupItemProps => {
    const {
      'data-automation-id': itemDataAutomationId,
      icon,
      id: itemIdProp,
      value: itemValue,
      disabled: itemIsDisabled,
    } = item;

    const ariaLabel = item['aria-label'] ? item['aria-label'] : undefined;
    const label = item.label ? item.label : undefined;

    const firstChild = index === 0;
    const checked = value === itemValue;
    const isDisabled = itemIsDisabled || disabled;
    const dataAutomationId =
      itemDataAutomationId || `visual-radio-button-${itemValue}`;

    /**
     * Ensure the first Button is tabbable, even there isn't any in the group
     * that's checked.
     */
    let tabIndex = checked ? 0 : -1;
    if (isNil(value) && index === 0) {
      tabIndex = 0;
    }

    const inputAutomationId = `hidden-radio-button-${itemValue}`;
    const buttonId = itemIdProp || `${id}-${itemValue}`;

    const eventHandlers = {
      onBlur: handleOnBlur,
      onFocus: handleOnFocus,
      onKeyDown: handleOnKeyDown,
      onMouseEnter: handleOnMouseEnter,
      onMouseLeave: handleOnMouseLeave,
    } as const;

    const buttonItemProps = {
      icon,
      isChecked: checked,
      key: buttonId,
      radiogroup,
      sizing,
      style: {
        borderRadius: getBorderRadius(index),
      },
      buttonProps: {
        ...eventHandlers,
        'aria-checked': checked,
        'aria-disabled': isDisabled,
        'aria-label': ariaLabel || label,
        'data-automation-id': dataAutomationId,
        disabled: isDisabled,
        firstChild,
        hasLabel: Boolean(label),
        id: buttonId,
        isChecked: checked,
        onBlur: handleOnBlur,
        onClick: () => handleOnChange(itemValue),
        onFocus: handleOnFocus,
        onKeyDown: handleOnKeyDown,
        onMouseEnter: handleOnMouseEnter,
        onMouseLeave: handleOnMouseLeave,
        role: 'radio',
        sizing,
        style: {
          borderRadius: getBorderRadius(index),
          ...tabStyles,
        },
        tabIndex: radiogroup ? null : checked ? 0 : -1,
      },
      inputProps: {
        ...eventHandlers,
        'aria-labelledby': buttonId,
        'data-automation-id': inputAutomationId,
        autoComplete: 'off',
        checked,
        disabled: isDisabled,
        name,
        onChange: () => handleOnChange(itemValue),
        tabIndex,
        type: 'radio',
      },
      radiogroupProps: {
        firstChild,
      },
      visualButtonProps: {
        'aria-checked': checked,
        'aria-disabled': isDisabled,
        'aria-hidden': true,
        'aria-label': ariaLabel,
        'data-automation-id': dataAutomationId,
        disabled: isDisabled,
        firstChild,
        hasLabel: Boolean(label),
        id: buttonId,
        isChecked: checked,
        role: 'radio',
        sizing,
        style: {
          borderRadius: getBorderRadius(index),
          ...tabStyles,
        },
      },
    } as const;

    if (item['aria-label'] !== undefined) {
      const result: EnhancedButtonGroupItemPropsWithAriaLabel = {
        ...buttonItemProps,
        'aria-label': item['aria-label'],
      };
      return result;
    } else {
      // We should know for sure that item.label is not undefined here:
      /*:: (item.label: React.Node) */
      const result: EnhancedButtonGroupItemPropsWithLabel = {
        ...buttonItemProps,
        label: item.label,
      };
      return result;
    }
  };

  return {
    ...rest,
    ...keyboardHandlers,
    getItemProps,
    id,
    options,
    role: 'radiogroup',
    ref: mergeRefs([ref, buttonGroupRef]),
    sizing,
  };
}
